import { useEffect, useState, useMemo } from 'react'
import Select from 'react-select'
import {
  GetNewHissaCompaniesQuery,
  SyncHissaCompaniesMutationVariables,
  useGetNewHissaCompaniesQuery,
  useSyncHissaCompaniesMutation,
  useGetAllFundsOfUserQuery,
  SyncHissaCompaniesMutation,
  useGetAllInvestmentsQuery,
  useGetInvestorsQuery,
} from '../../../../generated/graphql'
import { graphqlRequestClient, queryClient } from '../../../../queries/client'
import { selectCustomStyles } from '../../../../utils/Select'
import Toast, { ToastType } from '../../../../utils/toast'
import { toAbsoluteUrl, KTSVG } from '../../../../_metronic/helpers'

type Props = {
  show: boolean
  handleClose: () => void
  selectedUserId?: string
}

function SyncInvestorPopup({ show, handleClose, selectedUserId }: Props) {
  const [selectedInvestorId, setSelectedInvestorId] = useState<number | null>(null)

  const { data: fundsData, isLoading: isFundsLoading } = useGetAllFundsOfUserQuery(
    graphqlRequestClient,
    { input: selectedUserId },
    { enabled: !!selectedUserId && show }
  )

  const investorOptions =
    fundsData?.getAllFundsOfUser?.map((fund: any) => ({
      label: fund.name,
      value: fund.id,
    })) || []

  const { data, isLoading } = useGetNewHissaCompaniesQuery<GetNewHissaCompaniesQuery, Error>(
    graphqlRequestClient,
    { input: selectedInvestorId || -1 },
    {
      enabled: !!selectedInvestorId && selectedInvestorId !== -1,
      staleTime: 1000 * 60,
    }
  )

  const syncMutation = useSyncHissaCompaniesMutation<Error>(graphqlRequestClient, {
    onSuccess: (_data: SyncHissaCompaniesMutation, _vars: SyncHissaCompaniesMutationVariables) => {
      queryClient.invalidateQueries(
        useGetNewHissaCompaniesQuery.getKey({ input: selectedInvestorId! })
      )
      queryClient.invalidateQueries(useGetAllInvestmentsQuery.getKey())
      queryClient.invalidateQueries(useGetInvestorsQuery.getKey())
      queryClient.invalidateQueries(useGetAllFundsOfUserQuery.getKey({ input: selectedUserId }))

      Toast('Synced Companies Successfully!', ToastType.success)
      handleClose()
    },
    onError: (error: any) => {
      Toast(error.response?.errors?.[0]?.message || 'Sync Failed', ToastType.error)
    },
  })

  // Reset on close
  useEffect(() => {
    if (!show) {
      setSelectedInvestorId(null)
    }
  }, [show])

  // Optional: refetch when investor changes (you had this, but invalidate is enough)
  useEffect(() => {
    if (selectedInvestorId) {
      queryClient.invalidateQueries(
        useGetNewHissaCompaniesQuery.getKey({ input: selectedInvestorId })
      )
    }
  }, [selectedInvestorId])

  if (!show) return null
  return (
    <div
      className='modal fade show d-block'
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className='modal-dialog modal-dialog-centered' style={{ maxWidth: '700px' }}>
        <div className='modal-content'>
          <div className='modal-header'>
            <div className='col d-flex justify-content-between px-1'>
              <div className='py-3'>
                <h3 className='fw-bold text-dark'>Sync Investor Companies</h3>
              </div>
              <button
                className='btn btn-sm btn-icon btn-active-light-primary mt-0'
                type='button'
                data-bs-toggle='tooltip'
                title='Close'
                onClick={handleClose}
                disabled={syncMutation.isLoading}
              >
                <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
              </button>
            </div>
          </div>

          <div className='modal-body'>
            {/* Investor/Fund Selector */}
            <div className='mb-5'>
              <label className='form-label fw-semibold required'>Select Investor / Fund</label>
              <Select
                options={investorOptions}
                styles={selectCustomStyles}
                name='investor'
                placeholder='Select Investor or Fund'
                value={investorOptions.find((opt: any) => opt.value === selectedInvestorId) || null}
                onChange={(option: any) => setSelectedInvestorId(option?.value || null)}
                isDisabled={syncMutation.isLoading || isFundsLoading}
                isLoading={isFundsLoading}
              />
              {investorOptions.length === 0 && !isFundsLoading && (
                <div className='text-muted mt-2 fs-7'>
                  No funds available for this user. Please add a fund first.
                </div>
              )}
            </div>

            <div className='mb-4'>
              {(isLoading || isFundsLoading) && (
                <div className='text-center py-4'>
                  <span className='spinner-border spinner-border-sm me-2' />
                  <span className='text-muted'>Loading...</span>
                </div>
              )}

              {!isLoading && !isFundsLoading && (
                <>
                  {(!selectedInvestorId ||
                    (data?.getNewHissaCompaniesForInvestor?.length ?? 0) === 0) && (
                    <div className='text-center py-5'>
                      <img
                        alt='no companies'
                        src={toAbsoluteUrl('/media/custom-images/emptyRecentCompanies.svg')}
                        className='h-150px mb-4'
                      />
                      <h4 className='text-muted mb-2'>
                        {selectedInvestorId
                          ? 'No Companies Found for syncing'
                          : 'No Investor/Fund selected'}
                      </h4>
                      {!selectedInvestorId && (
                        <div className='fw-semibold fs-6 text-muted'>
                          Please select Investor/Fund above to view new companies
                        </div>
                      )}
                    </div>
                  )}

                  {/* Show list of companies if present */}
                  {selectedInvestorId && (data?.getNewHissaCompaniesForInvestor?.length ?? 0) > 0 && (
                    <div className='mb-4'>
                      <label className='form-label fw-semibold mb-3'>
                        Companies to Sync ({data?.getNewHissaCompaniesForInvestor?.length})
                      </label>
                      <div
                        className='border rounded p-3'
                        style={{ maxHeight: '300px', overflowY: 'auto' }}
                      >
                        {data?.getNewHissaCompaniesForInvestor?.map((item, index) => (
                          <div className='py-2 border-bottom' key={item?.companyId}>
                            <p className='fs-6 mb-0 fw-normal'>
                              {index + 1}. {item?.companyName}{' '}
                              <span className='text-muted'>({item?.companyId})</span>
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Action Buttons */}
            <div className='d-flex justify-content-end gap-3 mt-4'>
              <button
                type='button'
                className='btn btn-light'
                onClick={handleClose}
                disabled={syncMutation.isLoading}
              >
                Cancel
              </button>
              {selectedInvestorId && (data?.getNewHissaCompaniesForInvestor?.length ?? 0) > 0 && (
                <button
                  type='button'
                  className='btn btn-primary'
                  disabled={syncMutation.isLoading}
                  onClick={() => {
                    if (selectedInvestorId) syncMutation.mutate({ input: selectedInvestorId })
                  }}
                >
                  {syncMutation.isLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' />
                      Syncing...
                    </>
                  ) : (
                    'Sync Companies'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SyncInvestorPopup
